import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { search, updateQueryParams } from '../../../actions/search-actions';
import {
    SEARCH_REPLACE_FILTER_INPUTS,
    SEARCH_UPDATE_FILTERS_APPLIED,
} from '../../../actions/actionTypes/search-action-types';
import { minUtrValue, maxUtrValue } from '../../../utils/constants';
import { parseDistanceMi } from '../../../utils/distance';
import { CONFIDENCE_NEEDS_CLARIFICATION } from '../../../utils/naturalSearch';
import { FILTER_KEYS } from '../SearchV2/constants';

import styles from './InferredFilterChips.scss';

const {
    QUERY,
    UTR_MIN,
    UTR_MAX,
    LAT,
    LNG,
    DISTANCE,
    LOCATION,
    AGE_MIN,
    AGE_MAX,
    GENDER,
    GENDERS,
    EVENT_TYPES,
    BALL_TYPES,
    START_DATE,
    END_DATE,
} = FILTER_KEYS;

// 'standard' = the LLM's inferred filter values; 'grounded' = a value filled
// in from the caller's MemberContext (My location, My UTR level, etc.) —
// distinct styling so users see what we filled in for them.
const KIND_STANDARD = 'standard';
const KIND_GROUNDED = 'grounded';

// Single-character codes mirror the LLM's gender emission shape (M/F/X);
// elsewhere the codebase uses 'Male'/'Female' words (see utils/members.js
// MemberGenderLabels) which don't apply here. Keep this map local.
const GENDER_LABELS = { M: 'Men', F: 'Women', X: 'Mixed' };

// Tennis age-bucket thresholds — used to label well-known LLM age ranges
// ('Juniors', 'Youth', 'Seniors') instead of rendering bare numbers.
const JUNIORS_MAX_AGE = 18;
const YOUTH_MAX_AGE = 14;
const SENIORS_MIN_AGE = 50;

// Pull the set of truthy keys out of a {key: true} boolean dict. eventTypes /
// ballTypes / genders all carry this shape in state.search.filters (see
// searchApi.appendBooleanFilterEntries).
function activeBooleanDictKeys(dict) {
    if (!dict || typeof dict !== 'object') {
        return [];
    }
    return Object.entries(dict)
        .filter(([, v]) => v)
        .map(([k]) => k);
}

function buildQueryChip(filters) {
    if (!filters.query) {
        return null;
    }
    return {
        id: 'query',
        label: `"${filters.query}"`,
        kind: KIND_STANDARD,
        removes: [QUERY],
    };
}

// UTR range — collapse min/max into one chip. The universe-spanning UTR
// range (minUtrValue..maxUtrValue) is treated as "no UTR filter" so it
// doesn't produce noise.
function buildUtrChip(filters, resolvedContext) {
    const utrMinSet = filters.utrMin != null && filters.utrMin !== minUtrValue;
    const utrMaxSet = filters.utrMax != null && filters.utrMax !== maxUtrValue;
    if (!utrMinSet && !utrMaxSet) {
        return null;
    }
    const resetTo = { [UTR_MIN]: minUtrValue, [UTR_MAX]: maxUtrValue };
    if (resolvedContext?.usedSinglesUtr) {
        return {
            id: 'utr-grounded',
            label: 'My UTR level',
            kind: KIND_GROUNDED,
            removes: [UTR_MIN, UTR_MAX],
            resetTo,
        };
    }
    const min = utrMinSet ? filters.utrMin : '';
    const max = utrMaxSet ? filters.utrMax : '';
    const singleBound = min !== '' ? min : max;
    return {
        id: 'utr',
        label: min !== '' && max !== '' ? `UTR ${min}–${max}` : `UTR ${singleBound}+`,
        kind: KIND_STANDARD,
        removes: [UTR_MIN, UTR_MAX],
        resetTo,
    };
}

// Geographic radius — translateLlmFilters splits the server-resolved pin
// into separate lat/lng fields to match the existing redux/search shape.
// Chip label text comes from resolvedContext.locationLabel ("Near you" for
// the caller's home pin; the geocoder's formatted address for a named
// place); set server-side so the FE doesn't need to assemble it from
// region-name fields.
function buildLocationChip(filters, resolvedContext) {
    if (filters.lat == null || filters.lng == null) {
        return null;
    }
    if (resolvedContext?.usedHomeLocation) {
        const distNum = parseDistanceMi(filters.distance) ?? 25;
        return {
            id: 'location-grounded',
            label: `My location · ${distNum} mi`,
            kind: KIND_GROUNDED,
            removes: [LAT, LNG, DISTANCE, LOCATION],
        };
    }
    // lat/lng are set (guarded above), so always render a removable chip — even
    // without a distance — otherwise the location filter has no way to be cleared.
    const distNum = filters.distance ? parseDistanceMi(filters.distance) : null;
    const placeName = resolvedContext?.locationLabel || null;
    let label;
    if (placeName && distNum != null) {
        label = `Near ${placeName} · ${distNum} mi`;
    } else if (placeName) {
        label = `Near ${placeName}`;
    } else if (distNum != null) {
        label = `Within ${distNum} mi`;
    } else {
        label = 'Location';
    }
    return {
        id: 'location-radius',
        label,
        kind: KIND_STANDARD,
        removes: [LAT, LNG, DISTANCE, LOCATION],
    };
}

function buildAgeChip(filters, resolvedContext) {
    if (filters.ageMin == null && filters.ageMax == null) {
        return null;
    }
    if (resolvedContext?.usedAge) {
        return {
            id: 'age-grounded',
            label: 'My age',
            kind: KIND_GROUNDED,
            removes: [AGE_MIN, AGE_MAX],
        };
    }
    return {
        id: 'age',
        label: formatAgeLabel(filters.ageMin, filters.ageMax),
        kind: KIND_STANDARD,
        removes: [AGE_MIN, AGE_MAX],
    };
}

// Gender: LLM emits singular `gender` (player searches) or array `genders`
// (event searches); translateLlmFilters converts the array to a boolean
// dict, so read both shapes here.
function buildGenderChip(filters, resolvedContext) {
    const activeGenders = activeBooleanDictKeys(filters.genders);
    if (!filters.gender && activeGenders.length === 0) {
        return null;
    }
    const resetTo = { [GENDER]: '', [GENDERS]: {} };
    if (resolvedContext?.usedGender) {
        return {
            id: 'gender-grounded',
            label: 'My gender',
            kind: KIND_GROUNDED,
            removes: [GENDER, GENDERS],
            resetTo,
        };
    }
    const code = filters.gender || activeGenders[0];
    const label = activeGenders.length > 1
        ? activeGenders.map((c) => GENDER_LABELS[c] ?? c).join(', ')
        : (GENDER_LABELS[code] ?? code);
    return {
        id: 'gender',
        label,
        kind: KIND_STANDARD,
        removes: [GENDER, GENDERS],
        resetTo,
    };
}

function buildEventTypesChip(filters) {
    const activeEventTypes = activeBooleanDictKeys(filters.eventTypes);
    if (!activeEventTypes.length) {
        return null;
    }
    return {
        id: 'eventTypes',
        label: activeEventTypes.map(prettifyEventType).join(', '),
        kind: KIND_STANDARD,
        removes: [EVENT_TYPES],
        resetTo: { [EVENT_TYPES]: {} },
    };
}

function buildBallTypesChip(filters) {
    const activeBallTypes = activeBooleanDictKeys(filters.ballTypes);
    if (!activeBallTypes.length) {
        return null;
    }
    return {
        id: 'ballTypes',
        label: activeBallTypes.map((b) => `${capitalize(b)} ball`).join(', '),
        kind: KIND_STANDARD,
        removes: [BALL_TYPES],
        resetTo: { [BALL_TYPES]: {} },
    };
}

function buildDateChip(filters) {
    if (!filters.startDate && !filters.endDate) {
        return null;
    }
    return {
        id: 'date',
        label: formatDateLabel(filters.startDate, filters.endDate),
        kind: KIND_STANDARD,
        removes: [START_DATE, END_DATE],
    };
}

const CHIP_BUILDERS = [
    buildQueryChip,
    buildUtrChip,
    buildLocationChip,
    buildAgeChip,
    buildGenderChip,
    buildEventTypesChip,
    buildBallTypesChip,
    buildDateChip,
];

export function buildChips(filters, resolvedContext) {
    if (!filters) {
        return [];
    }
    return CHIP_BUILDERS
        .map((build) => build(filters, resolvedContext))
        .filter(Boolean);
}

function formatAgeLabel(min, max) {
    if (max === JUNIORS_MAX_AGE && min == null) {
        return 'Juniors';
    }
    if (max === YOUTH_MAX_AGE && min == null) {
        return 'Youth';
    }
    if (min === SENIORS_MIN_AGE && max == null) {
        return 'Seniors';
    }
    if (min != null && max != null) {
        return `Ages ${min}–${max}`;
    }
    if (max != null) {
        return `Under ${max}`;
    }
    return `${min}+`;
}

function formatDateLabel(start, end) {
    // Filters may carry moment instances (per searchReducer) or strings — be
    // defensive about both shapes.
    const fmt = (d) => {
        if (!d) {
            return '';
        }
        if (typeof d.format === 'function') {
            return d.format('MMM D');
        }
        return String(d);
    };
    if (start && end) {
        return `${fmt(start)} – ${fmt(end)}`;
    }
    if (start) {
        return `From ${fmt(start)}`;
    }
    return `Until ${fmt(end)}`;
}

function prettifyEventType(rawType) {
    // utr-search canonical values are snake_case (flex_league, paid_hit, ...).
    return rawType.split('_').map(capitalize).join(' ');
}

function capitalize(s) {
    if (!s) {
        return s;
    }
    return s.charAt(0).toUpperCase() + s.slice(1);
}

// Exported unconnected for tests.
export const InferredFilterChipsBase = ({
    confidence,
    filters,
    resolvedContext,
    onRemove,
}) => {
    if (!confidence || confidence === CONFIDENCE_NEEDS_CLARIFICATION) {
        return null;
    }

    const chips = buildChips(filters, resolvedContext);
    if (chips.length === 0) {
        return null;
    }

    return (
        <ul
            className={styles.chipRow}
            aria-label="Inferred search filters"
            data-testid="search.nl-search.chip-rail"
        >
            {chips.map((chip) => (
                <li
                    key={chip.id}
                    className={styles.chipItem}
                    data-testid={`search.nl-search.chip-${chip.id}.wrapper`}
                >
                    <button
                        type="button"
                        className={chip.kind === KIND_GROUNDED ? styles.chipGrounded : styles.chip}
                        onClick={() => onRemove(chip.removes, filters, chip.resetTo)}
                        aria-label={`Remove ${chip.label}`}
                        data-testid={`search.nl-search.chip-${chip.id}.remove-btn`}
                    >
                        <span
                            className={styles.chipLabel}
                            data-testid={`search.nl-search.chip-${chip.id}.label`}
                        >
                            {chip.label}
                        </span>
                        <span
                            className={styles.chipClose}
                            aria-hidden="true"
                            data-testid={`search.nl-search.chip-${chip.id}.close-icon`}
                        >
                            ×
                        </span>
                    </button>
                </li>
            ))}
        </ul>
    );
};

InferredFilterChipsBase.propTypes = {
    confidence: PropTypes.string,
    filters: PropTypes.shape({
        query: PropTypes.string,
        utrMin: PropTypes.number,
        utrMax: PropTypes.number,
        lat: PropTypes.number,
        lng: PropTypes.number,
        distance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        location: PropTypes.string,
        ageMin: PropTypes.number,
        ageMax: PropTypes.number,
        gender: PropTypes.string,
        genders: PropTypes.objectOf(PropTypes.bool),
        eventTypes: PropTypes.objectOf(PropTypes.bool),
        ballTypes: PropTypes.objectOf(PropTypes.bool),
        // searchReducer hydrates startDate/endDate to a moment instance; the
        // LLM emits an ISO string before translateLlmFilters normalizes it.
        startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
        endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    }),
    resolvedContext: PropTypes.shape({
        usedSinglesUtr: PropTypes.bool,
        usedHomeLocation: PropTypes.bool,
        usedAge: PropTypes.bool,
        usedGender: PropTypes.bool,
        locationLabel: PropTypes.string,
    }),
    onRemove: PropTypes.func.isRequired,
};

InferredFilterChipsBase.defaultProps = {
    confidence: null,
    filters: null,
    resolvedContext: null,
};

const mapStateToProps = (state) => ({
    confidence: state.naturalSearch?.confidence ?? null,
    filters: state.search?.filters ?? null,
    resolvedContext: state.naturalSearch?.resolvedContext ?? null,
});

const mapDispatchToProps = (dispatch) => ({
    onRemove: (keys, currentFilters, resetTo) => {
        const next = { ...currentFilters };
        keys.forEach((k) => {
            delete next[k];
        });
        if (resetTo) {
            // Restore defaults for keys that aren't truly removable (utrMin/utrMax,
            // boolean dicts) — REPLACE_FILTER_INPUTS overlays onto INITIAL_FILTERS
            // anyway, but being explicit here keeps the dispatch self-documenting.
            Object.assign(next, resetTo);
        }
        dispatch({ type: SEARCH_REPLACE_FILTER_INPUTS, filters: next });
        dispatch({ type: SEARCH_UPDATE_FILTERS_APPLIED });
        dispatch(search());
        dispatch(updateQueryParams(true));
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(InferredFilterChipsBase);
